import { Static, Type } from '@sinclair/typebox';
import { EmailChangeKind } from './translation_namespace/email_change_namespace';

/**
 * Changement d'adresse email vérifié — contact de l'établissement (`professional.email_contact`)
 * et email de connexion (`account.email`). Rien ne change tant que la nouvelle adresse n'a pas
 * confirmé ; l'ancienne est prévenue avec un lien d'annulation. Cf. backend `email_change/`.
 */

const maxEmailLength = 255;
const passwordMaxLength = 128;

const NewEmailField = Type.String({
  format: 'email',
  placeholder: 'INPUT_PLACEHOLDER_NEW_EMAIL',
  title: 'INPUT_TITLE_NEW_EMAIL',
  maxLength: maxEmailLength,
});

const CurrentPasswordField = Type.String({
  placeholder: 'INPUT_PASSWORD',
  title: 'INPUT_TITLE_CURRENT_PASSWORD',
  meta: { input_type: 'password' },
  minLength: 1,
  maxLength: passwordMaxLength,
});

/** Issue d'une demande : en attente de confirmation, ou appliquée tout de suite (invitation jamais acceptée). */
export enum EmailChangeRequestOutcome {
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
  APPLIED_INVITATION_RESENT = 'APPLIED_INVITATION_RESENT',
}

// ─── Demandes (app, authentifié) ─────────────────────────────────────

/** Email de contact : le mot de passe de l'OWNER est exigé DANS la requête (pas seulement la session). */
export const RequestContactEmailChangeRequestSchema = Type.Object({
  new_email: NewEmailField,
  current_password: CurrentPasswordField,
});
export type RequestContactEmailChangeRequestObject = Static<typeof RequestContactEmailChangeRequestSchema>;

/** Email de connexion : le mot de passe du TITULAIRE est demandé à la confirmation, pas ici. */
export const RequestLoginEmailChangeRequestSchema = Type.Object({
  new_email: NewEmailField,
});
export type RequestLoginEmailChangeRequestObject = Static<typeof RequestLoginEmailChangeRequestSchema>;

/**
 * Adresse de récupération : même preuve que l'email de contact — mot de passe du compte DANS la
 * requête, puis clic depuis la nouvelle adresse. Elle doit être externe (ni un domaine hébergé chez
 * nous, ni un domaine de la marque).
 */
export const RequestRecoveryEmailChangeRequestSchema = Type.Object({
  new_email: NewEmailField,
  current_password: CurrentPasswordField,
});
export type RequestRecoveryEmailChangeRequestObject = Static<typeof RequestRecoveryEmailChangeRequestSchema>;

export const PendingEmailChangeResponseSchema = Type.Object({
  email_change_request_id: Type.Number(),
  kind: Type.Enum(EmailChangeKind),
  new_email: Type.String(),
  expires_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'expires_at', with_time: true } }),
});
export type PendingEmailChangeResponseObject = Static<typeof PendingEmailChangeResponseSchema>;

export const RequestEmailChangeResponseSchema = Type.Object({
  outcome: Type.Enum(EmailChangeRequestOutcome),
  new_email: Type.String(),
  /** Demande créée (PENDING_CONFIRMATION) : la ligne Équipe l'affiche et peut l'annuler sans recharger. */
  email_change_request_id: Type.Optional(Type.Number()),
});
export type RequestEmailChangeResponseObject = Static<typeof RequestEmailChangeResponseSchema>;

export const ContactEmailStateResponseSchema = Type.Object({
  email_contact: Type.String(),
  /** Prénom + nom du (premier) OWNER : le message « droits insuffisants » dit à qui s'adresser. */
  owner_name: Type.Optional(Type.String()),
  /** Compte Stripe Connect présent : rappeler au pro de mettre à jour l'email côté Stripe lui-même. */
  has_stripe_connect: Type.Boolean(),
  pending: Type.Optional(PendingEmailChangeResponseSchema),
});
export type ContactEmailStateResponseObject = Static<typeof ContactEmailStateResponseSchema>;

export const LoginEmailStateResponseSchema = Type.Object({
  account_email: Type.String(),
  /** Faux = session ouverte par lien de reprise d'essai sans mot de passe : à définir d'abord. */
  has_password: Type.Boolean(),
  pending: Type.Optional(PendingEmailChangeResponseSchema),
  /** L'email de connexion est une boîte hébergée chez nous : l'adresse de récupération est obligatoire. */
  login_is_hosted: Type.Boolean(),
  /** Adresse de récupération VÉRIFIÉE (absente tant qu'aucune n'a été confirmée). */
  recovery_email: Type.Optional(Type.String()),
  /** Changement d'adresse de récupération en attente de confirmation. */
  recovery_pending: Type.Optional(PendingEmailChangeResponseSchema),
});
export type LoginEmailStateResponseObject = Static<typeof LoginEmailStateResponseSchema>;

// ─── Liens reçus par email (public) ──────────────────────────────────

export const EmailChangeTokenRequestSchema = Type.Object({
  token: Type.String({ minLength: 32, maxLength: 64 }),
});
export type EmailChangeTokenRequestObject = Static<typeof EmailChangeTokenRequestSchema>;

export const EmailChangeTokenInfoResponseSchema = Type.Object({
  kind: Type.Enum(EmailChangeKind),
  new_email: Type.String(),
  /** Nom de l'établissement (contact) ; absent pour un email de connexion ou de récupération. */
  pro_name: Type.Optional(Type.String()),
  /** Confirmation d'un email de connexion : le mot de passe du titulaire est exigé. */
  requires_password: Type.Boolean(),
});
export type EmailChangeTokenInfoResponseObject = Static<typeof EmailChangeTokenInfoResponseSchema>;

export const ConfirmEmailChangeRequestSchema = Type.Object({
  token: Type.String({ minLength: 32, maxLength: 64, meta: { hidden: true } }),
  password: Type.Optional(CurrentPasswordField),
});
export type ConfirmEmailChangeRequestObject = Static<typeof ConfirmEmailChangeRequestSchema>;

/** Formulaire de la page de confirmation d'un email de connexion (le token vient de l'URL). */
export const ConfirmEmailChangePasswordFormSchema = Type.Object({
  password: CurrentPasswordField,
});

export const ConfirmEmailChangeResponseSchema = Type.Object({
  kind: Type.Enum(EmailChangeKind),
  new_email: Type.String(),
  /** Email de contact d'un pro qui a un compte Stripe Connect : à mettre à jour chez Stripe par le pro. */
  stripe_connect_reminder: Type.Boolean(),
});
export type ConfirmEmailChangeResponseObject = Static<typeof ConfirmEmailChangeResponseSchema>;
